import "../styles/About.css";

const About = () => {
  const fashionBrands = [
    { id: 1, name: "Pensole Lewis College" },
    { id: 2, name: "New Balance" },
    { id: 3, name: "Foot Locker" },
    { id: 4, name: "Arts & Scraps" },
    { id: 5, name: "Nextiles" },
  ];
  const techBrands = [
    { id: 1, name: "GE Aviation" },
    { id: 2, name: "GE Digital" },
    { id: 3, name: "StockX" },
    { id: 4, name: "Code for America" },
    { id: 5, name: "Autograph.io" },
    { id: 6, name: "Just Air" },
    { id: 7, name: "TripSlip" },
    { id: 8, name: "Community Tech Alliance" },
  ];

  return (
    <div className="about">
      <section className="about-intro" id="about">
        <div className="profile-image">
          <img
            src="/headshot.JPG"
            alt="Headshot of Nerd"
            className="headshot"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="bio">
          <h1>About Nerd</h1>
          <p className="about-subhead">
            Fashion designer, Both Rows founder, and software engineering
            leader—building in cloth and code.
          </p>
          <p className="about-mission">
            I create so people can see themselves clearly—through what they wear
            and what they build. My work lives at the intersection of emotional
            storytelling and strong systems: one-of-one fashion rooted in craft
            and sustainability, and senior-level tech advisory that helps
            founders make confident decisions and ship products that last.
          </p>
          <div className="about-tags">
            <span className="about-tag">Both Rows / Sustainable Fashion</span>
            <span className="about-tag">Tech Advisory / Product Engineering</span>
          </div>
        </div>
      </section>

      <section className="about-section" id="why">
        <h2>Why Work With Me</h2>
        <p>
          I don’t approach work as a task executor—I operate as an owner.
          Whether I’m designing a garment or leading a technical team, my focus
          is on building things that hold up under real use. I bring clarity
          where things are vague, structure where things feel chaotic, and
          forward momentum when teams are stuck.
        </p>
        <p>
          I’ve served as a CTO, led engineering organizations, and partnered
          directly with founders through accelerators, product rebuilds, and
          investor conversations. I understand what’s at stake when you’re
          early—and I help you make decisions that won’t box you in later.
        </p>
        <ul className="about-proof">
          <li>Founder-level technical leadership and decision-making</li>
          <li>Zero-to-one system and team building</li>
          <li>Experience guiding products through high-risk inflection points</li>
          <li>Strong systems designed to evolve, not collapse</li>
          <li>Direct, honest partnership rooted in trust</li>
        </ul>
        <p className="about-proof-note">
          My pricing reflects the cost of mistakes I help you avoid and the
          momentum I help you create.
        </p>
      </section>

      <section className="about-section" id="story">
        <h2>My Story</h2>
        <div className="about-story-grid">
          <div className="about-story-block">
            <h3>Fashion + Identity</h3>
            <p>
              My journey in fashion began in vulnerability—standing in a
              closet, overwhelmed, trying to find something that would help me
              feel like myself. That experience taught me clothing is not just
              fabric. It can be armor, identity, and an invitation back to
              confidence. Over time, fashion became a tool for self-discovery
              and self-acceptance—and eventually, a way to help other people
              experience the same shift.
            </p>
            <p>
              As the founder of{" "}
              <a
                href="https://www.showbothrows.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Both Rows
              </a>
              , I design one-of-one pieces from upcycled materials and build
              each garment around a story. The name reflects duality I
              embrace—traditional craftsmanship and innovation, structure and
              softness, creator and curator. Mental health is woven into the
              brand: each piece includes a QR code linking to resources for
              emotional wellbeing and personal growth.
            </p>
          </div>
          <div className="about-story-block">
            <h3>Tech + Systems</h3>
            <p>
              In tech, I have spent my career helping teams turn messy ideas
              into real products—by clarifying scope, building the right
              architecture early, and creating delivery systems that do not
              collapse under pressure. I am at my best when partnering with
              founders and teams to make strong technical decisions, design
              scalable systems, and ship with clarity. The same way I approach
              garments—pattern, structure, fit—I approach software: intentional
              design, thoughtful execution, and room for evolution.
            </p>
          </div>
        </div>
      </section>

      <section className="about-section" id="now">
        <h2>What I Do Now</h2>
        <div className="about-card-grid">
          <div className="about-card">
            <h3>Both Rows - Fashion Design</h3>
            <ul>
              <li>Custom one-of-one pieces and upcycling transformations</li>
              <li>Reimagined classics with modern silhouette and storytelling</li>
              <li>Craft-forward garment construction and pattern work</li>
              <li>Sustainable design practices rooted in reuse and longevity</li>
              <li>Design consults to align fit, function, and identity</li>
            </ul>
          </div>
          <div className="about-card">
            <h3>Tech Services - Advisory + Engineering Support</h3>
            <ul>
              <li>Founder Tech Office Hours (decision support + strategy)</li>
              <li>Product direction + MVP planning (what to build first and why)</li>
              <li>
                Architecture reviews (scalability, reliability, security posture)
              </li>
              <li>
                Engineering leadership support (process, hiring, team structure)
              </li>
              <li>
                Implementation support for web systems as scoped, high-impact
                engagements
              </li>
            </ul>
            <p className="about-note">
              Note: Office Hours are advisory and do not include implementation
              outside scheduled sessions.
            </p>
          </div>
        </div>
      </section>

      {(fashionBrands.length > 0 || techBrands.length > 0) && (
        <section className="about-section" id="brands">
          <h2>Brand Collaborations</h2>
          <p className="about-credentials-subhead">
            A selection of fashion and tech partners I have supported.
          </p>
          <div className="about-brands-grid">
            <div className="about-brand-card">
              <h3>Fashion</h3>
              <div className="about-brand-list">
                {fashionBrands.map((brand) => (
                  <div key={brand.id} className="about-brand-item">
                    {brand.logo ? (
                      <img
                        src={brand.logo}
                        alt={`${brand.name} logo`}
                        className="about-brand-logo"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span>{brand.name}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="about-brand-card">
              <h3>Tech</h3>
              <div className="about-brand-list">
                {techBrands.map((brand) => (
                  <div key={brand.id} className="about-brand-item">
                    {brand.logo ? (
                      <img
                        src={brand.logo}
                        alt={`${brand.name} logo`}
                        className="about-brand-logo"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span>{brand.name}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="about-section" id="how">
        <h2>How I Work</h2>
        <ul className="about-proof">
          <li>Ownership mindset: I treat client problems like my own.</li>
          <li>Clarity over noise: I help teams decide what not to build.</li>
          <li>Systems thinking: I design for scale, change, and longevity.</li>
          <li>Craft matters: clean seams and clean code both matter.</li>
          <li>Direct communication: you’ll always know where things stand.</li>
        </ul>
      </section>

      <section className="about-section" id="credentials">
        <h2>Credentials & Impact</h2>
        <p className="about-credentials-subhead">
          Experience that reduces risk, accelerates clarity, and holds up under
          real use.
        </p>
        <div className="about-credentials-grid">
          <div className="about-credential-card">
            <h3>Founder-Level CTO Experience</h3>
            <ul>
              <li>Served as CTO for a Techstars-backed travel startup</li>
              <li>Led full frontend and backend rebuilds</li>
              <li>
                Partnered on product strategy, pitching, and investor readiness
              </li>
              <li>Owned zero-to-one technical decisions</li>
            </ul>
          </div>
          <div className="about-credential-card">
            <h3>Head of Engineering Leadership</h3>
            <ul>
              <li>Led engineering strategy for a production platform</li>
              <li>Owned backend systems, delivery processes, and team direction</li>
              <li>Balanced speed, reliability, and long-term maintainability</li>
            </ul>
          </div>
          <div className="about-credential-card">
            <h3>Senior Systems & Delivery</h3>
            <ul>
              <li>
                Built and scaled production systems across multiple environments
              </li>
              <li>
                Strengthened reliability through observability and incident
                readiness
              </li>
              <li>Reduced rework by designing systems to evolve</li>
            </ul>
          </div>
          <div className="about-credential-card">
            <h3>Craft & Design Foundations</h3>
            <ul>
              <li>
                Four-year seamstress apprenticeship in garment construction
              </li>
              <li>Technical fashion training (tech packs, digital workflows)</li>
              <li>
                Founder of Both Rows, creating one-of-one upcycled pieces
              </li>
            </ul>
          </div>
        </div>
        <p className="about-credentials-caption">
          This experience informs how I advise, what I recommend, and why my
          work is priced at a senior level.
        </p>
      </section>

      <section className="about-section" id="specialties">
        <h2>Specialties</h2>
        <div className="about-specialties-grid">
          <div>
            <h3>Fashion Specialties</h3>
            <div className="about-chip-grid">
              {[
                "Sustainable Design",
                "Garment Construction",
                "Upcycling and Rework",
                "Custom One-of-One Pieces",
                "Pattern Making",
                "Tech Pack Development",
                "Brand Development",
                "Mental Health-Integrated Design",
              ].map((skill) => (
                <span key={skill} className="about-chip">
                  {skill}
                </span>
              ))}
            </div>
          </div>
          <div>
            <h3>Tech Specialties</h3>
            <div className="about-chip-grid">
              {[
                "Product Strategy and MVP Sequencing",
                "System Design and Architecture (scalable foundations)",
                "Backend Services and APIs (Node/TypeScript)",
                "Frontend Delivery (React)",
                "Cloud and CI/CD Foundations",
                "Observability and Incident Readiness",
                "Security and Risk Review",
                "Engineering Leadership and Team Process",
              ].map((skill) => (
                <span key={skill} className="about-chip">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;
